import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { db } from "@/lib/firebase";
import { collection, addDoc, getDocs, deleteDoc, doc, query, orderBy } from "firebase/firestore";
import { incrementCounter } from "@/lib/stats";

export const contactSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  subject: z.string().min(5, "Subject must be at least 5 characters"),
  message: z.string().min(10, "Message must be at least 10 characters"),
});

export type ContactInput = z.infer<typeof contactSchema>;

export function useSubmitContact() {
  return useMutation({
    mutationFn: async (data: ContactInput) => {
      contactSchema.parse(data);
      const docRef = await addDoc(collection(db, "contact_messages"), {
        ...data,
        isRead: false,
        createdAt: new Date().toISOString(),
      });
      try {
        await incrementCounter("contact_messages");
      } catch (e) {}
      return { id: docRef.id, ...data };
    },
  });
}

export function useContactMessages() {
  return useQuery({
    queryKey: ["contactMessages"],
    queryFn: async () => {
      const q = query(collection(db, "contact_messages"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      return snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    },
  });
}

export function useRemoveContactMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await deleteDoc(doc(db, "contact_messages", id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contactMessages"] });
    },
  });
}
